'use client'

import type { ServerActionResponse, ServerActionSuccess } from '@maestro/core'

import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useCallback } from 'react'
import {
  type DefaultValues,
  type FieldErrors,
  type Path,
  type SubmitHandler,
  useForm as useReactHookForm,
} from 'react-hook-form'
import { z, ZodObject, ZodOptional, type ZodRawShape, ZodType } from 'zod'

import { useNotificationContext } from '@maestro/commerce-blocks/components/notification/notification.context'
import { useFormTranslations } from '@maestro/commerce-blocks/hooks/use-form-translations'
import { getTranslatedFormErrors } from '@maestro/commerce-blocks/utils/form-error.utils'
import { trimStringValues } from '@maestro/commerce-blocks/utils/form.utils'

interface UseFormProps<
  FormSchema extends ZodType,
  SuccessData extends ServerActionSuccess = ServerActionSuccess,
> {
  schema: FormSchema
  submitAction: (
    rawData: z.infer<FormSchema>
  ) => Promise<ServerActionResponse<z.infer<FormSchema>, SuccessData>>
  onSuccess?: (
    response: SuccessData,
    submittedData?: z.infer<FormSchema>
  ) => void
  defaultValues?: DefaultValues<z.infer<FormSchema>>
  formContext?: string
  readOnly?: boolean
}

export function useForm<
  FormSchema extends ZodObject<ZodRawShape>,
  SuccessData extends ServerActionSuccess = ServerActionSuccess,
>({
  schema,
  submitAction,
  onSuccess = undefined,
  defaultValues,
  formContext,
  readOnly = false,
}: UseFormProps<FormSchema, SuccessData>) {
  const formT = useFormTranslations(formContext)
  const t = useTranslations('commerce-blocks.form')

  const { showError } = useNotificationContext()

  const {
    register,
    handleSubmit: handleSubmitReactHookForm,
    setError,
    control,
    getValues,
    setValue,
    formState: { errors, isSubmitting, dirtyFields },
    reset,
    getFieldState,
    unregister,
  } = useReactHookForm<z.infer<FormSchema>>({
    // Type assertion needed due to Zod v4 input/output type compatibility with React Hook Form
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(schema) as any,
    defaultValues,
    mode: readOnly ? 'onSubmit' : 'onTouched',
  })

  const registerField = useCallback(
    (name: Path<z.infer<FormSchema>>) => {
      const getSchemaField = (fieldPath: string) => {
        const parts = fieldPath.split('.')
        let current: ZodType | undefined = schema

        for (const part of parts) {
          if (current instanceof ZodObject) {
            current = current.shape[part]
          } else {
            return undefined
          }
        }
        return current
      }

      const schemaField = getSchemaField(name)
      const required = !(schemaField instanceof ZodOptional)
      const fieldState = getFieldState(name)
      const value = getValues(name)
      const success =
        (fieldState.isDirty || value) && !fieldState.invalid ? true : undefined
      return {
        ...register(name),
        required,
        success,
        optionalText: required ? undefined : t('general.optional-text'),
        disabled: isSubmitting,
      }
    },
    [getFieldState, getValues, isSubmitting, register, schema, t]
  )

  const translatedErrors = getTranslatedFormErrors(
    errors,
    formT
  ) as FieldErrors<z.infer<FormSchema>>

  const onSubmit: SubmitHandler<z.infer<FormSchema>> = useCallback(
    async (formData) => {
      const normalizedFormData = trimStringValues(formData)
      const response = await submitAction(normalizedFormData)

      if (response.success) {
        onSuccess?.(response, normalizedFormData)
        return
      }

      if (response.errorType === 'general') {
        showError(
          response.errorMessage.title,
          response.errorMessage.description
        )
        return
      }

      if (response.message) {
        setError('root.serverError', {
          type: 'server',
          message: response.message,
        })
      }

      if (response.errors) {
        for (const [field, errors] of Object.entries(response.errors)) {
          if (errors) {
            // @ts-expect-error set errors
            setError(field as keyof FormSchema, {
              type: 'server',
              message: errors.join('; '),
            })
          }
        }
      }
    },
    [submitAction, onSuccess, setError, showError]
  )

  const handleSubmit = useCallback(
    (e: React.BaseSyntheticEvent) => {
      e.preventDefault()
      handleSubmitReactHookForm(onSubmit)(e)
    },
    [handleSubmitReactHookForm, onSubmit]
  )

  const isSubmitDisabled =
    isSubmitting ||
    Object.keys(errors).filter((error) => error !== 'root').length > 0

  return {
    register: registerField,
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: {
      errors: translatedErrors,
      isSubmitting,
      isSubmitDisabled,
      dirtyFields,
    },
    formT,
    reset,
    unregister,
    getFieldState,
  }
}
